import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { AppService } from '@/app.service';
import { SessionService } from '@/logging.services';
import { loginService } from '@/curent-user-connexion.service';

declare const window: any;

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly LINK_URL: string = environment.URL_LINK;
  PNS_AUTH_MODE: boolean = true;

  name?: string;
  npi?: string;

  selectedLanguage: 'fr' | 'en' = 'fr';

  stateChange = false;
  currentData: any;

  // Modal instances (Bootstrap) - initialized in ngAfterViewInit
  private deconnexionModalInstance: any | null = null;
  private emailAuthModalInstance: any | null = null;

  private destroy$ = new Subject<void>();

  @ViewChild('deconnexionModal', { static: false })
  private deconnexionModalRef?: ElementRef<HTMLDivElement>;
  @ViewChild('emailAuthModal', { static: false })
  private emailAuthModalRef?: ElementRef<HTMLDivElement>;

  constructor(
    private router: Router,
    private appService: AppService,
    private loginService: loginService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private sessionService: SessionService
  ) {
    // Setup available languages
    this.translate.addLangs(['en', 'fr']);
    this.translate.setDefaultLang('fr');

    // If route contains a lang param, use it
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const lang = params['lang'];
      if (lang) {
        this.translate.use(lang.match(/en|fr/) ? lang : 'en');
      }
    });
  }

  ngOnInit(): void {
    this.sessionService.setHomeVariable(false);

    // load persisted user info if available
    const userData = this.getStoredData();
    if (userData) {
      this.name = userData?.nom;
      this.npi = userData?.NPI;
    }

    this.initializeStateSubscription();
  }

  ngAfterViewInit(): void {
    // Initialize Bootstrap modals only after view init and only if bootstrap is available
    if (typeof window !== 'undefined' && window?.bootstrap) {
      if (this.deconnexionModalRef?.nativeElement) {
        this.deconnexionModalInstance = new window.bootstrap.Modal(
          this.deconnexionModalRef.nativeElement
        );
      }
      if (this.emailAuthModalRef?.nativeElement) {
        this.emailAuthModalInstance = new window.bootstrap.Modal(
          this.emailAuthModalRef.nativeElement
        );
      }
    }
  }

  private initializeStateSubscription(): void {
    // initial value
    this.currentData = this.loginService.getCurrentUserData();
    this.updateStateChange();

    // subscribe to future changes and auto-unsubscribe
    this.loginService.userData$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.currentData = data;
      this.updateStateChange();
    });
  }

  private updateStateChange(): void {
    this.stateChange = this.currentData === true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  switchLanguage(language: 'fr' | 'en'): void {
    this.translate.use(language);
    this.selectedLanguage = language;
  }

  logOut(): void {
    // clear local state then call backend logout
    localStorage.clear();
    this.router.navigate(['/']);

    // Call logout endpoint; hide modal and reload on success/failure
    this.appService
      ._systemLogout()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          localStorage.clear();
          this.deconnexionModalInstance?.hide();
          this.router.navigate(['/']);
          this.initializeStateSubscription();
          window.location.reload();
        },
        () => {
          localStorage.clear();
          this.router.navigate(['/']);
          window.location.reload();
        }
      );
  }

  openAuthentication(): void {
    this.emailAuthModalInstance?.show();
  }

  closeAuthentication(): void {
    this.emailAuthModalInstance?.hide();
  }

  closeDeconnexion(): void {
    this.deconnexionModalInstance?.hide();
  }

  // Backwards-compatible aliases for existing template bindings
  authentication(): void {
    this.openAuthentication();
  }

  closedModalForAuthentication(): void {
    this.closeAuthentication();
  }

  closedModalForDeconnexion(): void {
    this.closeDeconnexion();
  }

  token(): string | null {
    return window?.localStorage?.getItem('token') ?? null;
  }

  private getStoredData(): any | null {
    const item = window?.localStorage?.getItem('globalData');
    return item ? JSON.parse(item) : null;
  }
}
